import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import { 
  CheckCircle, 
  ExpandMore,
  Star, 
  WorkOutline, 
  TrendingUp,
  ArrowBack,
  LinkedIn,
  Assignment,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const ResumeGuidePage: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);

  useEffect(() => {
    document.title = 'Resume & LinkedIn Guide | Ray Jones';
  }, []);

  const handleFaqChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const painPoints = [
    "Your resume gets ignored despite having the technical skills",
    "You can't figure out why similar candidates get interviews while you don't", 
    "Your LinkedIn profile feels invisible to recruiters and hiring managers",
    "You're applying to hundreds of jobs but getting zero responses",
    "You know you're qualified but can't communicate your value effectively",
    "Every job posting feels like a lottery ticket instead of a sure thing",
  ];

  const benefits = [
    "Your resume immediately catches recruiters' attention and stands out from the pile",
    "You understand exactly why top candidates get chosen and replicate their success",
    "Your LinkedIn profile becomes a magnet that attracts hiring managers actively",
    "You get interview requests from quality companies in your field",
    "You confidently showcase your technical skills in a way that excites employers",
    "Every application feels strategic and targeted, dramatically improving your success rate",
  ];

  const includedItems = [
    {
      title: "The Exact Resume That Got Me My Tech Job Offer",
      description: "My actual resume template with real data that landed me a $120k+ offer - see exactly how I formatted, positioned, and worded everything",
      icon: <Assignment />,
    },
    {
      title: "LinkedIn Optimization Blueprint",
      description: "Step-by-step guide that helped 5 engineers get interviews - optimize your headline, summary, experience, and networking strategy",
      icon: <LinkedIn />,
    },
    {
      title: "ATS-Friendly Resume Checklist",
      description: "Ensure your resume passes through Applicant Tracking Systems that 95% of companies use to filter candidates",
      icon: <CheckCircle />,
    },
    {
      title: "Technical Skills Positioning Framework",
      description: "How to present your programming languages, frameworks, and projects in the most compelling way possible",
      icon: <Star />,
    },
    {
      title: "LinkedIn Headline Formulas That Work",
      description: "5 proven headline templates that get you noticed by recruiters (used by all 5 engineers who got interviews)",
      icon: <TrendingUp />,
    },
    {
      title: "Project Description Templates",
      description: "Turn boring project descriptions into compelling stories that demonstrate real business impact",
      icon: <WorkOutline />,
    },
  ];

  const faqs = [
    {
      question: "Is this just another generic resume template?",
      answer: "Absolutely not. This is my actual resume that got me hired, not some generic template. You'll see real project descriptions, real experience formatting, and the exact positioning that worked in the real world. Plus, the LinkedIn guide has a 100% success rate - all 5 engineers who followed it got interviews within 30 days.",
    },
    {
      question: "What if I'm a new graduate or career changer?",
      answer: "Perfect! The resume template shows how to position yourself when you don't have tons of experience yet. I was a new graduate when this resume got me hired. The LinkedIn guide specifically covers how to build credibility and network when you're just starting out in tech.",
    },
    {
      question: "Will this work for my specific tech stack?",
      answer: "Yes. The positioning principles work for any programming language or framework. You'll see how to adapt the technical skills section for your specific stack, whether you're doing web development, data science, mobile development, or any other area of software engineering.",
    },
    {
      question: "How quickly will I see results?",
      answer: "Most people see immediate improvements in their application response rates within the first week of implementing the resume changes. For LinkedIn, the 5 engineers who followed the guide got interview requests within 30 days. However, I can't guarantee specific results - it depends on your market, experience level, and how well you implement the strategies.",
    },
    {
      question: "What if I'm not satisfied?",
      answer: "I'm confident this will help you, but if you're not satisfied within 30 days, just email me and I'll refund your $11. No questions asked. I want you to succeed, not just take your money.",
    },
    {
      question: "Why only $11? What's the catch?",
      answer: "No catch. I could charge $100+ like other career coaches, but I'd rather help more people at a price everyone can afford. My main business is 1-on-1 coaching at $11,999/year. This is my way of giving back to the community and helping people who can't afford premium coaching yet.",
    },
    {
      question: "How is this different from free resume advice online?",
      answer: "Free advice is generic and theoretical. This is my actual resume that worked in the real world, plus a LinkedIn strategy with a 100% success rate among people who followed it. You're getting proven systems, not theory. Most free advice comes from people who haven't actually gotten hired recently.",
    },
    {
      question: "Can I get this for free in the community?",
      answer: "Yes! If you join our $7/month community for software engineers, you get this guide plus ongoing resume reviews, LinkedIn optimization help, and networking with other developers. The community is designed to give you ongoing support, not just one-time materials.",
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      
      {/* Hero Section */}
      <Box sx={{ pt: 12, pb: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Audience Callout */}
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', mb: 3, fontWeight: 'medium' }}
            >
              For Software Engineers Who Are Tired of Being Ignored
            </Typography>
            
            {/* Bold Promise */}
            <Typography
              variant="h2"
              gutterBottom
              sx={{ color: 'text.primary', mb: 3, fontWeight: 'bold' }}
            >
              <span style={{ color: '#88C0D0' }}>Get More Tech Interview Invites</span> This Week
            </Typography>
            
            {/* Summary */}
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', mb: 6, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}
            >
              Get instant access to my exact resume that landed me a $120k+ tech job offer, plus my proven LinkedIn optimization guide that helped 5 software engineers get interviews within 30 days, complete ATS-friendly formatting checklist, technical skills positioning framework, and project description templates that turn boring code into compelling business impact stories.
            </Typography>

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.location.href = '/checkout?product=resume-guide'}
                sx={{
                  color: 'black',
                  px: 6,
                  py: 2,
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get Instant Access - $11
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  px: 6,
                  py: 2,
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'rgba(136, 192, 208, 0.1)',
                  },
                }}
                href="https://www.skool.com/raymond/about"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get It FREE in $7 Community
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pain Points Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            If Any of This <span style={{ color: '#88C0D0' }}>Sounds Like You...</span>
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 8 }}>
            {painPoints.map((pain, index) => (
              <Box key={index}>
                <Card sx={{ 
                  bgcolor: 'rgba(255, 0, 0, 0.05)', 
                  border: '1px solid rgba(255, 0, 0, 0.2)',
                  height: '100%',
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                      😣 {pain}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{ color: 'text.secondary', mb: 4, fontStyle: 'italic' }}
            >
              You're not alone. I've been there, and thousands of other talented engineers are struggling with the same exact problems.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            Imagine If <span style={{ color: '#88C0D0' }}>Instead...</span>
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 8 }}>
            {benefits.map((benefit, index) => (
              <Box key={index}>
                <Card sx={{ 
                  bgcolor: 'rgba(0, 255, 0, 0.05)', 
                  border: '1px solid rgba(0, 255, 0, 0.2)',
                  height: '100%',
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                      ✅ {benefit}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{ color: 'secondary.main', mb: 4, fontWeight: 'bold' }}
            >
              This isn't a pipe dream. This is exactly what happens when you have the right resume and LinkedIn strategy.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Solution Introduction */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 4 }}
          >
            Introducing: <span style={{ color: '#88C0D0' }}>The Resume & LinkedIn Bundle</span>
          </Typography>
          
          <Typography
            variant="h5"
            align="center"
            sx={{ color: 'text.secondary', mb: 6, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}
          >
            This is the exact resume and LinkedIn strategy that has proven to work in the real world, giving you everything you need to stand out in today's competitive tech job market.
          </Typography>
        </Container>
      </Box>

      {/* Discovery Story */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            How I <span style={{ color: '#88C0D0' }}>Discovered</span> This System
          </Typography>
          
          <Paper sx={{ p: 6, bgcolor: 'background.paper', borderRadius: 4 }}>
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              Three years ago, I was just another computer science student sending out dozens of applications and getting crickets. Despite having solid projects and good grades, my resume was being ignored. I was frustrated, broke, and starting to doubt if I'd ever break into tech.
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              Then I got obsessed. I started reverse-engineering successful resumes, studying what worked, and testing different approaches. I analyzed job postings, learned how ATS systems actually work, and discovered the psychological triggers that make recruiters stop scrolling.
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              After 6 months of iteration and testing, I created the resume that changed everything. Within 2 weeks of using this new version, I got multiple interview requests. Within a month, I had a $120k+ job offer in hand.
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              But I didn't stop there. I realized LinkedIn was just as important, so I developed a systematic approach to optimization that turned my profile into a recruiter magnet. I tested this with other engineers I was mentoring, and all 5 of them got interview requests within 30 days.
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'secondary.main', fontSize: '1.2rem', lineHeight: 1.8, fontWeight: 'bold' }}>
              This isn't theory or generic advice. This is a battle-tested system that has worked for me and every engineer who's implemented it properly. Nobody else can give you this because nobody else lived through this exact journey and documented what actually works.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* What's Included Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            What's <span style={{ color: '#88C0D0' }}>Included</span>
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4, mb: 8 }}>
            {includedItems.map((item, index) => (
              <Box key={index}>
                <Card sx={{ 
                  bgcolor: 'background.default', 
                  height: '100%',
                  border: '2px solid #333',
                  '&:hover': {
                    border: '2px solid #88C0D0',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ mr: 2, color: 'secondary.main' }}>
                        {item.icon}
                      </Box>
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        {item.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Paper sx={{ 
              p: 4, 
              bgcolor: 'rgba(136, 192, 208, 0.1)', 
              border: '2px solid #88C0D0',
              borderRadius: 4,
              maxWidth: 600,
              mx: 'auto',
              mb: 6,
            }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Less than a fancy coffee, but this could change your entire career trajectory
              </Typography>
            </Paper>

            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.location.href = '/checkout?product=resume-guide'}
                sx={{
                  color: 'black',
                  px: 6,
                  py: 2,
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get Instant Access - $11
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  px: 6,
                  py: 2,
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'rgba(136, 192, 208, 0.1)',
                  },
                }}
                href="https://www.skool.com/raymond/about"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get It FREE in $7 Community
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            Frequently Asked <span style={{ color: '#88C0D0' }}>Questions</span>
          </Typography>
          
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion 
                key={index}
                expanded={expandedFaq === `panel${index}`}
                onChange={handleFaqChange(`panel${index}`)}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid #333',
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    border: '1px solid #88C0D0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: 'secondary.main' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      margin: '16px 0',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 4, 
                bgcolor: 'rgba(136, 192, 208, 0.1)',
                border: '2px solid #88C0D0',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: '#88C0D0',
                },
                fontSize: '1.1rem',
                fontWeight: 'bold',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              ⚠️ Don't let another week pass with a resume that gets ignored. Your dream job could be just one optimized application away.
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.location.href = '/checkout?product=resume-guide'}
                sx={{
                  color: 'black',
                  px: 8,
                  py: 3,
                  fontWeight: 'bold',
                  fontSize: '1.4rem',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get Your Resume Bundle Now - $11
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  px: 8,
                  py: 3,
                  fontWeight: 'bold',
                  fontSize: '1.4rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'rgba(136, 192, 208, 0.1)',
                  },
                }}
                href="https://www.skool.com/raymond/about"
                target="_blank"
                rel="noopener noreferrer"
              >
                Join $7 Community Instead
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Back to Home */}
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          color="secondary"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontWeight: 'bold',
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
              bgcolor: 'rgba(136, 192, 208, 0.1)',
            },
          }}
          startIcon={<ArrowBack />}
        >
          Back to Home
        </Button>
      </Box>
    </Box>
  );
};

export default ResumeGuidePage;